import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import { apiService } from "../../backend/api";
import { Save, Loader2, ScanLine, Bell } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";

const AdminScanSettings = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    freeLimits: { daily: 3, weekly: 10, monthly: 20 },
    paidAlertThreshold: { daily: 20 },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await apiService.getScanSettings();
        setSettings({
          freeLimits: data.freeLimits || { daily: 3, weekly: 10, monthly: 20 },
          paidAlertThreshold: data.paidAlertThreshold || { daily: 20 },
        });
      } catch (err) {
        showNotification(t("adminDashboard.scanSettings.loadError", "Fehler beim Laden der Scan-Einstellungen"), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateScanSettings(settings);
      showNotification(t("adminDashboard.scanSettings.saved", "Einstellungen gespeichert"), "success");
    } catch (err) {
      showNotification(t("adminDashboard.scanSettings.saveError", "Fehler beim Speichern") + ": " + (err.message || ""), "error");
    } finally {
      setSaving(false);
    }
  };

  const updateFreeLimit = (field, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    setSettings(prev => ({
      ...prev,
      freeLimits: { ...prev.freeLimits, [field]: num },
    }));
  };

  const updatePaidThreshold = (field, value) => {
    const num = Math.max(1, parseInt(value) || 1);
    setSettings(prev => ({
      ...prev,
      paidAlertThreshold: { ...prev.paidAlertThreshold, [field]: num },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Paid User Alert Threshold */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-500/10 rounded-lg">
            <Bell size={20} className="text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("adminDashboard.scanSettings.paidAlertTitle", "Benachrichtigung bei Vielnutzung")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("adminDashboard.scanSettings.paidAlertDescription", "Sie erhalten eine E-Mail, wenn ein zahlender Nutzer diesen Schwellenwert überschreitet")}
            </p>
          </div>
        </div>

        <div className="max-w-xs">
          <label className="block text-sm font-medium text-muted-foreground mb-1.5">
            {t("adminDashboard.scanSettings.paidAlertDaily", "Scans pro Tag (Schwellenwert)")}
          </label>
          <Input
            type="number"
            min="1"
            value={settings.paidAlertThreshold.daily}
            onChange={(e) => updatePaidThreshold("daily", e.target.value)}
          />
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {t("adminDashboard.scanSettings.save", "Speichern")}
        </Button>
      </div>
    </div>
  );
};

export default AdminScanSettings;
